/**
 * DesignEdge - Editable edge for network design with distance and notes
 */
import React, { useState } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getBezierPath,
  useReactFlow,
} from '@xyflow/react';

export interface DesignEdgeData {
  distance: number; // meters
  loss: number; // dB
  notes?: string;
}

export const DesignEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}) => {
  const { updateEdgeData, deleteElements } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const edgeData = (data as unknown as DesignEdgeData) || { distance: 100, loss: 0.03, notes: '' };

  const handleDelete = () => {
    if (window.confirm('Zmazať toto prepojenie kábla?')) {
      deleteElements({ edges: [{ id }] });
    }
  };

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const handleDistanceChange = (newDistance: number) => {
    const loss = newDistance * 0.0003; // 0.35 dB/km = 0.0003 dB/m
    updateEdgeData(id, {
      ...edgeData,
      distance: newDistance,
      loss,
    });
  };

  const handleNotesChange = (newNotes: string) => {
    updateEdgeData(id, {
      ...edgeData,
      notes: newNotes,
    });
  };

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: selected ? '#667eea' : '#999',
          strokeWidth: selected ? 3 : 2,
          strokeDasharray: selected ? '5,5' : 'none',
          animation: selected ? 'dash 0.5s linear infinite' : 'none',
        }}
      />
      {/* Add animation keyframes */}
      <style>
        {`
          @keyframes dash {
            to {
              stroke-dashoffset: -10;
            }
          }
        `}
      </style>
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          {isEditing ? (
            <div
              style={{
                background: 'white',
                padding: '8px',
                borderRadius: '6px',
                border: '2px solid #667eea',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                minWidth: '200px',
              }}
            >
              <div style={{ marginBottom: '8px' }}>
                <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Distance (m):
                </label>
                <input
                  type="number"
                  value={edgeData.distance}
                  onChange={(e) => handleDistanceChange(parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    padding: '4px 8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px',
                  }}
                />
              </div>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Poznámky:
                </label>
                <input
                  type="text"
                  value={edgeData.notes || ''}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  placeholder="Nepovinná poznámka…"
                  style={{
                    width: '100%',
                    padding: '4px 8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setIsEditing(false)}
                  style={{
                    flex: 1,
                    padding: '6px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  Done
                </button>
                <button
                  onClick={handleDelete}
                  style={{
                    padding: '6px 12px',
                    background: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                  title="Zmazať prepojenie"
                >
                  🗑️
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => setIsEditing(true)}
              style={{
                background: 'white',
                padding: '6px 10px',
                borderRadius: '6px',
                border: `2px solid ${selected ? '#667eea' : '#ddd'}`,
                fontSize: '11px',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                color: '#333',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                if (!selected) {
                  e.currentTarget.style.borderColor = '#ddd';
                }
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <div style={{ color: '#666' }}>📏 {edgeData.distance}m</div>
              <div style={{ color: '#999', fontSize: '10px' }}>
                Loss: {edgeData.loss.toFixed(3)} dB
              </div>
              {edgeData.notes && (
                <div style={{ color: '#667eea', fontSize: '10px', marginTop: '2px' }}>
                  📝 {edgeData.notes}
                </div>
              )}
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

